<template>
  <div class="filter-bar flex flex-col bg-surface-0 dark:bg-surface-900 md:max-w-full">
    <section class="filter-bar--filters-wrapper w-full">
      <div class="flex flex-wrap items-end gap-6 w-full">
        <div class="filter-bar--search-wrapper flex-1 basis-40 min-w-[10rem]">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Search</label
          >
          <InputText
            id="search"
            v-model="searchValue"
            class="w-full"
            icon="pi pi-search"
            placeholder="Search by Case ID, Supplier ID, or JIRA Key"
            size="small"
          />
        </div>

        <div class="filter-bar--merchant-wrapper flex-1 basis-40 min-w-[10rem]">
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Supplier</label
          >
          <MultiSelect
            id="merchant"
            v-model="merchantSelected"
            :options="merchantOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Supplier"
            selectedItemsLabel="Multiple suppliers selected"
            :maxSelectedLabels="1"
            :resetFilterOnHide="true"
            size="small"
          />
        </div>

        <div
          class="filter-bar--alert-type-wrapper flex-1 basis-40 min-w-[10rem]"
        >
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Alert Type</label
          >
          <MultiSelect
            id="alert-type"
            v-model="alertTypeSelected"
            :options="alertTypeOptions"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            :showClear="true"
            :filter="true"
            placeholder="Alert Type"
            selectedItemsLabel="{0} alert types"
            :maxSelectedLabels="1"
            :resetFilterOnHide="true"
            size="small"
            data-test-id="cases-alert-type-filter"
          />
        </div>

        <div
          class="filter-bar--date-range-wrapper flex-[1.5] basis-64 min-w-[15rem]"
        >
          <label class="block text-xs font-medium text-surface-600 dark:text-surface-300 mb-1"
            >Date Range</label
          >
          <div class="flex gap-2">
            <DatePicker
              id="date-start"
              v-model="dateStart"
              placeholder="Start Date"
              size="small"
              show-icon
              input-format="yyyy-MM-dd"
              class="w-full"
            />
            <DatePicker
              id="date-end"
              v-model="dateEnd"
              placeholder="End Date"
              size="small"
              show-icon
              input-format="yyyy-MM-dd"
              class="w-full"
            />
          </div>
        </div>

        <div
          class="filter-bar--reset-wrapper flex justify-end items-end gap-3 shrink-0 ms-auto"
        >
          <Button
            label="Reset"
            variant="outlined"
            @click="reset"
            icon="pi pi-times"
            size="small"
          />
          <!-- Slot for custom refresh button, with default fallback -->
          <slot name="refresh-button" :refresh="refreshCases">
            <Button
              label="Refresh"
              @click="refreshCases"
              icon="pi pi-refresh"
              size="small"
            />
          </slot>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { storeToRefs } from 'pinia'
import MultiSelect from 'primevue/multiselect'
import DatePicker from 'primevue/datepicker'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import { useFCCCaseStore } from '@/stores/fcc'
import { useFCCMerchantStore } from '@/stores/fcc'
import { buildEventTypeOptions } from '@/util/fcc/eventTypeCatalogue'
import { useFrontegg } from '@frontegg/vue'

const { authState } = useFrontegg()
const caseStore = useFCCCaseStore()
const merchantStore = useFCCMerchantStore()
const { cases, filters } = storeToRefs(caseStore)

// Merchant options - populated from cases data with merchant names
const merchantOptions = computed(() => {
  const uniqueIds = Array.from(
    new Set(cases.value.filter(c => c.MERCHANT_ID).map(c => c.MERCHANT_ID)),
  ) as number[]

  return uniqueIds.map(id => {
    const merchant = merchantStore.getMerchantById(id)
    const label = merchant?.MERCHANT_NAME ? merchant.MERCHANT_NAME : `Supplier ${id}`
    return { value: id, label }
  })
})
const merchantSelected = computed({
  get: () => filters.value.merchant.selected,
  // The clear icon emits null: uncoerced it makes filteredCases throw on
  // .length, killing every filter on the page.
  set: (value: number[] | null) => {
    caseStore.updateFilters({
      merchant: {
        selected: value ?? [],
        options: filters.value.merchant.options,
      },
    })
  },
})

// Live catalogue from GET /fcc/event-types, fetched into the merchant store on
// FCC mount — computed so the dropdown fills in when that request lands.
const alertTypeOptions = computed(() =>
  buildEventTypeOptions(merchantStore.eventTypes),
)
const alertTypeSelected = computed({
  get: () => filters.value.alertType.selected,
  set: (value: string[] | null) => {
    caseStore.updateFilters({ alertType: { selected: value ?? [] } })
  },
})

// Date range
const dateStart = ref<Date | null>(
  filters.value.dateRange.start
    ? new Date(filters.value.dateRange.start)
    : null,
)
const dateEnd = ref<Date | null>(
  filters.value.dateRange.end ? new Date(filters.value.dateRange.end) : null,
)
const searchValue = ref<string>(filters.value.search.value || '')

// Watch for changes and update store
watch(dateStart, newValue => {
  caseStore.updateFilters({
    dateRange: {
      ...filters.value.dateRange,
      start: newValue ? newValue.toISOString().split('T')[0] : '',
    },
  })
})

watch(dateEnd, newValue => {
  caseStore.updateFilters({
    dateRange: {
      ...filters.value.dateRange,
      end: newValue ? newValue.toISOString().split('T')[0] : '',
    },
  })
})

watch(searchValue, newValue => {
  caseStore.updateFilters({ search: { value: newValue } })
})

// Watch store changes to update local refs
watch(
  () => filters.value.dateRange,
  newDateRange => {
    dateStart.value = newDateRange.start ? new Date(newDateRange.start) : null
    dateEnd.value = newDateRange.end ? new Date(newDateRange.end) : null
  },
  { deep: true },
)

watch(
  () => filters.value.search.value,
  newSearch => {
    searchValue.value = newSearch || ''
  },
)

// Reset the filters
function reset() {
  caseStore.resetFilters()
  dateStart.value = null
  dateEnd.value = null
  searchValue.value = ''
}

function refreshCases() {
  caseStore.fetchCases(authState)
}

// Expose refresh function for parent components to use
defineExpose({ refreshCases })
</script>

<style scoped>
/*
 * The clear icon sits over the label. Padding alone won't hold a long selection
 * off it — overflow clips at the padding box — so shrink the clipping container.
 */
.filter-bar :deep(#alert-type .p-multiselect-label-container) {
  margin-inline-end: 1.5rem;
}
</style>
